/**
 * @fileoverview Custom hook for handling modal keyboard and click events.
 * Manages escape key handling and click-outside-to-close functionality.
 */

import { useEffect } from "react";

/**
 * Props for the useModalEventHandlers hook.
 */
interface UseModalEventHandlersProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Whether the link dropdown is visible */
  readonly showDropdown: boolean;
  /** Setter for link dropdown visibility */
  readonly setShowDropdown: (show: boolean) => void;
  /** Whether the parent dropdown is visible */
  readonly showParentDropdown: boolean;
  /** Setter for parent dropdown visibility */
  readonly setShowParentDropdown: (show: boolean) => void;
  /** Handler for canceling/closing the modal */
  readonly onCancel: () => void;
}

/**
 * Custom hook for managing modal event handlers.
 * Handles escape key presses and clicks outside of dropdowns.
 *
 * @param props - Hook configuration
 */
export function useModalEventHandlers({
  isOpen,
  showDropdown,
  setShowDropdown,
  showParentDropdown,
  setShowParentDropdown,
  onCancel,
}: UseModalEventHandlersProps): void {
  useEffect(() => {
    /**
     * Handles escape key presses.
     * Closes dropdowns first, then the modal if no dropdowns are open.
     *
     * @param e - Keyboard event
     */
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showDropdown) {
          setShowDropdown(false);
        } else if (showParentDropdown) {
          setShowParentDropdown(false);
        } else {
          onCancel();
        }
      }
    };

    /**
     * Handles clicks outside of dropdown elements.
     * Closes dropdowns when clicking outside their container.
     *
     * @param e - Mouse event
     */
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (showDropdown && !target.closest(".dropdown-container")) {
        setShowDropdown(false);
      }
      if (showParentDropdown && !target.closest(".parent-dropdown-container")) {
        setShowParentDropdown(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [
    isOpen,
    showDropdown,
    showParentDropdown,
    setShowDropdown,
    setShowParentDropdown,
    onCancel,
  ]);
}
